import React, { useState, useEffect } from 'react';
import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import StyledModal from '../../../components/ui/StyledModal';
import StyledTable from '../../../components/ui/StyledTable';


import Icon from '../../../components/AppIcon';
import { automationRulesService } from '../../../services/automationRulesService';

const AutomationRulesModal = ({ isOpen, onClose }) => {
  const [rules, setRules] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [ruleStats, setRuleStats] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadRules();
    }
  }, [isOpen]);

  const loadRules = async () => {
    try {
      setIsLoading(true);
      const data = await automationRulesService?.getAllRules();
      setRules(data);
    } catch (error) {
      console.error('Error loading rules:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleRule = async (ruleId, currentStatus) => {
    try {
      await automationRulesService?.toggleRuleStatus(ruleId, !currentStatus);
      await loadRules();
    } catch (error) {
      alert(error?.message || 'فشل تغيير حالة القاعدة');
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!confirm('هل أنت متأكد من حذف هذه القاعدة؟')) return;

    try {
      await automationRulesService?.deleteRule(ruleId);
      await loadRules();
    } catch (error) {
      alert(error?.message || 'فشل حذف القاعدة');
    }
  };

  const handleViewStats = async (rule) => {
    try {
      const stats = await automationRulesService?.getRuleStatistics(rule?.id);
      setRuleStats({ ...stats, ruleName: rule?.ruleName });
      setShowStatsModal(true);
    } catch (error) {
      alert(error?.message || 'فشل تحميل الإحصائيات');
    }
  };

  const getTriggerTypeLabel = (type) => {
    const labels = {
      contract_created: 'إنشاء عقد',
      contract_updated: 'تحديث عقد',
      contract_approved: 'الموافقة على عقد',
      contract_value_threshold: 'عتبة قيمة العقد',
      property_type_match: 'مطابقة نوع العقار',
      client_segment_match: 'مطابقة فئة العميل'
    };
    return labels?.[type] || type;
  };

  const getActionTypeLabel = (type) => {
    const labels = {
      assign_contract_type: 'تعيين نوع العقد',
      schedule_followup: 'جدولة متابعة',
      generate_document: 'إنشاء مستند',
      send_notification: 'إرسال إشعار',
      auto_approve: 'موافقة تلقائية',
      apply_fee_structure: 'تطبيق هيكل الرسوم'
    };
    return labels?.[type] || type;
  };

  const stats = [
    {
      label: 'إجمالي القواعد',
      value: rules?.length,
      icon: 'Zap',
      iconColor: '#F59E0B',
      gradient: 'from-amber-500/20 to-amber-600/10',
      border: 'border-amber-500/30',
      labelColor: 'text-amber-400',
      valueColor: 'text-amber-400',
    },
    {
      label: 'نشط',
      value: rules?.filter(r => r?.isActive)?.length,
      icon: 'CheckCircle',
      iconColor: '#10B981',
      gradient: 'from-green-500/20 to-green-600/10',
      border: 'border-green-500/30',
      labelColor: 'text-green-400',
      valueColor: 'text-green-400',
    },
    {
      label: 'معطل',
      value: rules?.filter(r => !r?.isActive)?.length,
      icon: 'XCircle',
      iconColor: '#6B7280',
      gradient: 'from-gray-500/20 to-gray-600/10',
      border: 'border-gray-500/30',
      labelColor: 'text-gray-400',
      valueColor: 'text-gray-400',
    },
  ];

  const columns = [
    {
      header: 'اسم القاعدة',
      accessor: 'ruleName',
      className: 'w-1/3',
      render: (row) => (
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-1">{row?.ruleName}</h3>
          <p className="text-xs text-muted-foreground truncate">{row?.description}</p>
        </div>
      ),
    },
    {
      header: 'المحفز',
      accessor: 'triggerType',
      className: 'w-1/5',
      render: (row) => (
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-blue-500/20 rounded">
            <Icon name="Zap" size={12} color="#3B82F6" />
          </div>
          <span className="text-xs text-muted-foreground">
            {getTriggerTypeLabel(row?.triggerType)}
          </span>
        </div>
      ),
    },
    {
      header: 'الإجراء',
      accessor: 'actionType',
      className: 'w-1/5',
      render: (row) => (
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-green-500/20 rounded">
            <Icon name="Play" size={12} color="#10B981" />
          </div>
          <span className="text-xs text-muted-foreground truncate">
            {getActionTypeLabel(row?.actionType)}
          </span>
        </div>
      ),
    },
    {
      header: 'الحالة',
      accessor: 'isActive',
      className: 'w-24 text-center',
      render: (row) => (
        <button
          onClick={(e) => {
            e?.stopPropagation();
            handleToggleRule(row?.id, row?.isActive);
          }}
          className="group relative"
        >
          <div
            className={`w-10 h-5 rounded-full transition-colors ${
              row?.isActive ? 'bg-green-500' : 'bg-gray-600'
            }`}
          >
            <div
              className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                row?.isActive ? 'right-0.5' : 'right-5'
              }`}
            />
          </div>
        </button>
      ),
    },
    {
      header: 'الإجراءات',
      accessor: 'actions',
      className: 'w-32 text-center',
      render: (row) => (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={(e) => {
              e?.stopPropagation();
              handleViewStats(row);
            }}
            className="p-1.5 hover:bg-blue-500/20 rounded transition-colors"
            title="عرض الإحصائيات"
          >
            <Icon name="BarChart3" size={16} color="#3B82F6" />
          </button>
          <button
            onClick={(e) => {
              e?.stopPropagation();
              setSelectedRule(row);
            }}
            className="p-1.5 hover:bg-amber-500/20 rounded transition-colors"
            title="تعديل"
          >
            <Icon name="Edit" size={16} color="#F59E0B" />
          </button>
          <button
            onClick={(e) => {
              e?.stopPropagation();
              handleDeleteRule(row?.id);
            }}
            className="p-1.5 hover:bg-red-500/20 rounded transition-colors"
            title="حذف"
          >
            <Icon name="Trash2" size={16} color="#EF4444" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <StyledModal
      isOpen={isOpen}
      onClose={onClose}
      title="قواعد الأتمتة"
      size="xl"
      variant="amber"
      showStats={true}
      stats={stats}
      icon="Zap"
      iconColor="#F59E0B"
      className=""
      footer={null}
    >
      <div className="space-y-6" dir="rtl">
        {/* Header Actions */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-muted-foreground">
            إدارة قواعد if-then التلقائية للعقود والمتابعات
          </p>
          <Button
            variant="default"
            size="sm"
            onClick={() => setShowCreateModal(true)}
          >
            <Icon name="Plus" size={16} />
            <span className="mr-2">قاعدة جديدة</span>
          </Button>
        </div>

        {/* Rules List */}
        {isLoading ? (
          <div className="text-center py-12">
            <Icon name="Loader" size={32} color="var(--color-primary)" className="animate-spin mx-auto" />
            <p className="text-sm text-muted-foreground mt-4">جاري تحميل القواعد...</p>
          </div>
        ) : rules?.length === 0 ? (
          <div className="text-center py-12 border border-dashed border-border rounded-lg">
            <Icon name="Zap" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
            <p className="text-foreground font-medium mb-2">لا توجد قواعد أتمتة</p>
            <p className="text-sm text-muted-foreground mb-4">ابدأ بإنشاء قاعدة if-then لأتمتة سير العمل</p>
            <Button variant="outline" onClick={() => setShowCreateModal(true)}>
              إنشاء قاعدة جديدة
            </Button>
          </div>
        ) : (
          <StyledTable
            columns={columns}
            data={rules}
            variant="amber"
            emptyMessage="لا توجد قواعد أتمتة"
            emptyIcon="Zap"
            onRowClick={() => {}}
            selectedRow={null}
            onSelectRow={() => {}}
            onSelectAll={() => {}}
            className=""
          />
        )}

        {/* Info Box */}
        <div className="p-4 rounded-lg bg-primary/10 border border-primary/20">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} color="var(--color-primary)" />
            <div className="text-right">
              <p className="text-sm font-medium text-foreground mb-1">
                كيف تعمل قواعد الأتمتة؟
              </p>
              <p className="text-xs text-muted-foreground">
                عند حدوث المحفز (مثل إنشاء عقد)، يتم تقييم الشروط. إذا تحققت جميع الشروط، يتم تنفيذ الإجراءات تلقائياً.
                يمكنك استخدام الذكاء الاصطناعي (OpenAI و Anthropic) في الإجراءات لإنشاء المستندات والمراجعة التلقائية.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Create Rule Modal */}
      {showCreateModal && (
        <CreateRuleModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            loadRules();
          }}
          initialData={null}
        />
      )}

      {/* Edit Rule Modal */}
      {selectedRule && (
        <CreateRuleModal
          isOpen={!!selectedRule}
          onClose={() => setSelectedRule(null)}
          onSuccess={() => {
            setSelectedRule(null);
            loadRules();
          }}
          initialData={selectedRule}
        />
      )}

      {/* Stats Modal */}
      {showStatsModal && ruleStats && (
        <Modal
          isOpen={showStatsModal}
          onClose={() => setShowStatsModal(false)}
          title={`إحصائيات: ${ruleStats?.ruleName}`}
          size="md"
          icon="BarChart"
          iconColor="var(--color-primary)"
          footer={null}
        >
          <div className="space-y-4" dir="rtl">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 rounded-lg bg-muted/30 border border-border">
                <p className="text-sm text-muted-foreground mb-1">إجمالي التنفيذات</p>
                <p className="text-2xl font-bold text-foreground">{ruleStats?.totalExecutions}</p>
              </div>
              <div className="p-4 rounded-lg bg-muted/30 border border-border">
                <p className="text-sm text-muted-foreground mb-1">التنفيذات الناجحة</p>
                <p className="text-2xl font-bold text-green-400">{ruleStats?.successfulExecutions}</p>
              </div>
              <div className="p-4 rounded-lg bg-muted/30 border border-border">
                <p className="text-sm text-muted-foreground mb-1">الإجراءات الفاشلة</p>
                <p className="text-2xl font-bold text-red-400">{ruleStats?.failedActions}</p>
              </div>
              <div className="p-4 rounded-lg bg-muted/30 border border-border">
                <p className="text-sm text-muted-foreground mb-1">معدل النجاح</p>
                <p className="text-2xl font-bold text-primary">{ruleStats?.successRate}%</p>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </StyledModal>
  );
};

// Create Rule Modal Component
const CreateRuleModal = ({ isOpen, onClose, onSuccess, initialData }) => {
  const [formData, setFormData] = useState({
    ruleName: '',
    description: '',
    triggerType: '',
    priority: 0,
    isActive: true
  });
  const [conditions, setConditions] = useState([{
    field: '',
    operator: '',
    value: '',
    logicalOperator: 'AND'
  }]);
  const [actions, setActions] = useState([{
    type: '',
    config: {}
  }]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const triggerTypeOptions = [
    { value: 'contract_created', label: 'إنشاء عقد' },
    { value: 'contract_updated', label: 'تحديث عقد' },
    { value: 'contract_approved', label: 'الموافقة على عقد' },
    { value: 'contract_value_threshold', label: 'عتبة قيمة العقد' },
    { value: 'property_type_match', label: 'مطابقة نوع العقار' },
    { value: 'client_segment_match', label: 'مطابقة فئة العميل' }
  ];

  const operatorOptions = [
    { value: 'equals', label: 'يساوي' },
    { value: 'not_equals', label: 'لا يساوي' },
    { value: 'greater_than', label: 'أكبر من' },
    { value: 'less_than', label: 'أقل من' },
    { value: 'contains', label: 'يحتوي على' },
    { value: 'in_list', label: 'في القائمة' }
  ];

  const actionTypeOptions = [
    { value: 'assign_contract_type', label: 'تعيين نوع العقد' },
    { value: 'schedule_followup', label: 'جدولة متابعة' },
    { value: 'generate_document', label: 'إنشاء مستند' },
    { value: 'send_notification', label: 'إرسال إشعار' },
    { value: 'auto_approve', label: 'موافقة تلقائية' },
    { value: 'apply_fee_structure', label: 'تطبيق هيكل الرسوم' }
  ];

  const handleAddCondition = () => {
    setConditions([...conditions, { field: '', operator: '', value: '', logicalOperator: 'AND' }]);
  };

  const handleRemoveCondition = (index) => {
    setConditions(conditions?.filter((_, i) => i !== index));
  };

  const handleAddAction = () => {
    setActions([...actions, { type: '', config: {} }]);
  };

  const handleRemoveAction = (index) => {
    setActions(actions?.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!formData?.ruleName || !formData?.triggerType) {
      alert('الرجاء إدخال اسم القاعدة ونوع المحفز');
      return;
    }

    if (conditions?.some(c => !c?.field || !c?.operator)) {
      alert('الرجاء إكمال جميع الشروط');
      return;
    }

    if (actions?.some(a => !a?.type)) {
      alert('الرجاء إكمال جميع الإجراءات');
      return;
    }

    setIsSubmitting(true);

    try {
      await automationRulesService?.createRule({
        ...formData,
        conditions: conditions?.map(c => ({
          field: c?.field,
          operator: c?.operator,
          value: c?.value,
          logicalOperator: c?.logicalOperator
        })),
        actions: actions?.map(a => ({
          type: a?.type,
          config: a?.config
        }))
      });

      alert('تم إنشاء القاعدة بنجاح!');
      onSuccess();
    } catch (error) {
      alert(error?.message || 'فشل إنشاء القاعدة');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="إنشاء قاعدة أتمتة جديدة"
      size="xl"
      footer={null}
      icon="Plus"
      iconColor="var(--color-primary)"
    >
      <div className="space-y-6" dir="rtl">
        {/* Basic Info */}
        <div className="space-y-4">
          <Input
            label="اسم القاعدة"
            value={formData?.ruleName}
            onChange={(e) => setFormData({ ...formData, ruleName: e?.target?.value })}
            placeholder="مثال: عقود عالية القيمة - تسويق تلقائي"
            required
          />
          <Input
            label="الوصف"
            value={formData?.description}
            onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
            placeholder="وصف مختصر للقاعدة"
          />
          <Select
            label="نوع المحفز"
            options={triggerTypeOptions}
            value={formData?.triggerType}
            onChange={(value) => setFormData({ ...formData, triggerType: value })}
            placeholder="اختر المحفز"
            required
          />
          <Input
            label="الأولوية (0-100)"
            type="number"
            value={formData?.priority}
            onChange={(e) => setFormData({ ...formData, priority: parseInt(e?.target?.value) || 0 })}
            min="0"
            max="100"
          />
        </div>

        {/* Conditions */}
        <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-heading font-semibold text-foreground flex items-center gap-2">
              <Icon name="Filter" size={20} color="#3B82F6" />
              الشروط (IF)
            </h3>
            <Button variant="outline" size="sm" onClick={handleAddCondition}>
              <Icon name="Plus" size={16} />
              <span className="mr-2">إضافة شرط</span>
            </Button>
          </div>
          <div className="space-y-3">
            {conditions?.map((condition, index) => (
              <div key={index} className="flex items-end gap-2">
                {index > 0 && (
                  <Select
                    options={[
                      { value: 'AND', label: 'AND' },
                      { value: 'OR', label: 'OR' }
                    ]}
                    value={condition?.logicalOperator}
                    onChange={(value) => {
                      const newConditions = [...conditions];
                      newConditions[index].logicalOperator = value;
                      setConditions(newConditions);
                    }}
                  />
                )}
                <Input
                  placeholder="الحقل (مثال: contract_value)"
                  value={condition?.field}
                  onChange={(e) => {
                    const newConditions = [...conditions];
                    newConditions[index].field = e?.target?.value;
                    setConditions(newConditions);
                  }}
                />
                <Select
                  options={operatorOptions}
                  value={condition?.operator}
                  onChange={(value) => {
                    const newConditions = [...conditions];
                    newConditions[index].operator = value;
                    setConditions(newConditions);
                  }}
                  placeholder="المعامل"
                />
                <Input
                  placeholder="القيمة"
                  value={condition?.value}
                  onChange={(e) => {
                    const newConditions = [...conditions];
                    newConditions[index].value = e?.target?.value;
                    setConditions(newConditions);
                  }}
                />
                {conditions?.length > 1 && (
                  <button
                    onClick={() => handleRemoveCondition(index)}
                    className="p-2 rounded-lg hover:bg-red-500/10 transition-colors"
                  >
                    <Icon name="X" size={20} color="#EF4444" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-heading font-semibold text-foreground flex items-center gap-2">
              <Icon name="Play" size={20} color="#10B981" />
              الإجراءات (THEN)
            </h3>
            <Button variant="outline" size="sm" onClick={handleAddAction}>
              <Icon name="Plus" size={16} />
              <span className="mr-2">إضافة إجراء</span>
            </Button>
          </div>
          <div className="space-y-3">
            {actions?.map((action, index) => (
              <div key={index} className="flex items-end gap-2">
                <Select
                  options={actionTypeOptions}
                  value={action?.type}
                  onChange={(value) => {
                    const newActions = [...actions];
                    newActions[index].type = value;
                    setActions(newActions);
                  }}
                  placeholder="نوع الإجراء"
                />
                <Input
                  placeholder={'الإعدادات (JSON مثال: {"days_ahead": 7})'}
                  value={JSON.stringify(action?.config)}
                  onChange={(e) => {
                    try {
                      const newActions = [...actions];
                      newActions[index].config = JSON.parse(e?.target?.value || '{}');
                      setActions(newActions);
                    } catch (err) {
                      // Invalid JSON, ignore
                    }
                  }}
                />
                {actions?.length > 1 && (
                  <button
                    onClick={() => handleRemoveAction(index)}
                    className="p-2 rounded-lg hover:bg-red-500/10 transition-colors"
                  >
                    <Icon name="X" size={20} color="#EF4444" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Submit */}
        <div className="flex items-center justify-start gap-3 pt-4 border-t border-border">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            إلغاء
          </Button>
          <Button variant="default" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'جاري الإنشاء...' : 'إنشاء القاعدة'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AutomationRulesModal;